// @ts-nocheck
/*
 * Port of views/dashboard/user-wise-campaign-summary.js.
 * API: API_DASHBOARD_USER_WISE_CAMPAIGN_SUMMARY_STATS.
 * Column count preserved: 8 (User ID, Username, Total Campaigns, Total Numbers,
 * Calls Dialed, DnD Count, Connected Calls, Pending Calls).
 * DataTable handles sort / search (userId + username via searchColumnId) /
 * pagination / CSV export.
 */
import { useEffect, useMemo, useState } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import type { ColumnDef } from '@tanstack/react-table';
import { getApiCall } from '@/service/httpCall';
import { API_DASHBOARD_USER_WISE_CAMPAIGN_SUMMARY_STATS } from '@/service/url';
import { USER_ROLES } from '@/utilities/constant';
import { Storage } from '@/utilities/setting';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/data-table/DataTable';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';

export default function UserWiseCampaignSummary() {
  const today = new Date().toISOString().split('T')[0];
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = usePersistedState('dashboard/UserWiseCampaignSummary:filters', { startDate: today, endDate: today, userId: '' });
  const [loggedInUserRole, setLoggedInUserRole] = useState(null);

  useEffect(() => {
    async function checkUserRole() {
      const rawUser = Storage.getItem('user');
      if (!rawUser) return null;
      try {
        const user = JSON.parse(rawUser);
        if (user.role) setLoggedInUserRole(user.role);
      } catch (e) {}
    }
    checkUserRole();
  }, []);

  useEffect(() => {
    if (!loggedInUserRole) return;
    if (loggedInUserRole == USER_ROLES.CALL_CENTRE_AGENT) return;
    fetchData();

  }, [loggedInUserRole]);

  const fetchData = () => {
    if (!filters.startDate || !filters.endDate) {
      toast.error('Start and End date required');
      return;
    }
    setLoading(true);
    let query = `${API_DASHBOARD_USER_WISE_CAMPAIGN_SUMMARY_STATS}?startDate=${filters.startDate}&endDate=${filters.endDate}`;
    if (filters.userId) query += `&userId=${filters.userId}`;
    getApiCall(query)
      .then((resp) => {
        const formatted = (resp.data || resp).map((item) => ({
          ...item,
          userId: item.userId?.toString(),
          username: item.username?.toString(),
        }));
        setData(formatted);
      })
      .catch((err) => toast.error(friendlyApiError(err) || 'Failed to fetch campaign summary'))
      .finally(() => setLoading(false));
  };

  const clearFilters = () => {
    setFilters({ startDate: today, endDate: today, userId: '' });
    fetchData();
  };

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      { accessorKey: 'userId', header: 'User ID' },
      { accessorKey: 'username', header: 'Username' },
      { accessorKey: 'totalCampaigns', header: 'Total Campaigns' },
      { accessorKey: 'totalNumbers', header: 'Total Numbers' },
      { accessorKey: 'callsDialed', header: 'Calls Dialed' },
      { accessorKey: 'dndCount', header: 'DnD Count' },
      { accessorKey: 'connectedCalls', header: 'Connected Calls' },
      { accessorKey: 'pendingCalls', header: 'Pending Calls' },
    ],
    [],
  );

  return (
    <>
      <div className="mb-3 text-sm font-bold text-foreground">User-Wise Campaign Summary</div>

      <DateRangeFilter
        compact
        className="mb-3"
        fromLabel="Start Date"
        toLabel="End Date"
        value={{ from: filters.startDate, to: filters.endDate }}
        onChange={({ from, to }) => setFilters({ ...filters, startDate: from, endDate: to })}
        onApply={fetchData}
        applyLabel="Search"
        onReset={clearFilters}
      >
        <div>
          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">User ID</label>
          <Input
            type="text"
            placeholder="Enter User ID"
            value={filters.userId}
            onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
            className="h-9 w-40"
          />
        </div>
      </DateRangeFilter>

      <DataTable
        tableId="dashboard.user-wise-campaign-summary"
        columns={columns}
        data={data}
        isLoading={loading}
        searchPlaceholder="Search by username or user ID…"
        emptyTitle="No data"
        emptyDescription="No data available for the selected date range."
        pageSize={10}
        exportFilename="user-wise-campaign-summary.csv"
      />
    </>
  );
}
